import { useEffect, useRef, useState } from "react";

import { capitalize } from "lodash";

import type { ISite } from "@similarweb/ui-components";

import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useAIInput } from "app/pages/activation/hooks/setup/useAIInput";
import { useAISuggestions } from "app/pages/activation/hooks/setup/useAISuggestions";
import usePasteWithImages from "app/pages/activation/hooks/setup/usePasteWithImages";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { AIChips } from "app/pages/activation/components/SetupModal/CompetitorsStep/AIChips/AIChips";
import { activationTracking } from "app/pages/activation/services/tracking";

import { ONE } from "../../Header/constants";
import { AILoader } from "../../Shared/AILoader/AiLoader";
import CountryDropdown from "../CountryDropdown/CountryDropdown";
import { CompetitorsStepKeys } from "../dataAutomationKeys";

import { AIInput } from "./AIInput/AIInput";
import { CompetitorsDropdown } from "./CompetitorsDropdown/CompetitorsDropdown";
import CompetitorsList from "./CompetitorsList/CompetitorsList";
import { COMPETITORS_WRAPPER_ID } from "./constants";
import { getSelectedCompetitorsNamesList, getUpdatedMap } from "./helpers";
import {
    CompetitorsLimitText,
    LoaderWrapper,
    SelectCountryTitle,
    SelectedDomainTitle,
    SuggestedItemsListWrapper,
    WrappedSelect,
    WrappedSelectGroup,
    Wrapper,
} from "./styles";
import * as SC from "./styles";
import { competitorsStepTexts, TITLE_TEXT } from "./texts";
import type { CompetitorsMap, CompetitorsStepSchema, CompetitorSchema } from "./types";

const CompetitorsStep = ({
    selectedPrimaryEntity,
    selectedCompetitors,
    showWebsiteSelect,
    selectedCountry,
    onCompetitorSelect,
    onCountrySelect,
    setPrimary,
    setSelectedCompetitors,
    savedThreadId,
    setSavedThreadId,
    handleFocus,
    handleBlur,
    isOpen,
}: CompetitorsStepSchema) => {
    const autocompleteRef = useRef(null);

    const eventForScroll = useRef(false);
    const track = activationTracking.setup;

    const { prompt, promptText, onChange, handleSetPrompt, resetPrompt, onClickChip } = useAIInput();

    const {
        suggestionsAI,
        suggestedPrompts,
        isLoading: isAISuggestionLoading,
    } = useAISuggestions(
        selectedPrimaryEntity?.name,
        selectedCountry,
        prompt,
        selectedCompetitors,
        resetPrompt,
        savedThreadId,
        setSavedThreadId,
    );

    const [competitorsList, setCompetitorsList] = useState<CompetitorsMap>(new Map());

    const { onPasteDataUpdater, isLoading: isLoadingPaste } = usePasteWithImages(
        selectedCompetitors,
        competitorsList,
        selectedPrimaryEntity?.name,
    );

    const isMultiCountry = swSettings.components?.Activation?.allowedCountries?.length > ONE;
    const maxCompetitors = ActivationComponentAssets.competitorsLimit;
    const isLimitReached = selectedCompetitors.length >= maxCompetitors;

    useEffect(() => {
        eventForScroll.current = true;

        if (suggestionsAI.size && selectedPrimaryEntity && selectedCountry) {
            setCompetitorsList(suggestionsAI);
        }
    }, [suggestionsAI]);

    const onCompetitorAdd = (newCompetitor: CompetitorSchema) => {
        if (!newCompetitor) {
            return;
        }

        const isCompetitorInList = competitorsList.has(newCompetitor.name);

        if (!isCompetitorInList) {
            track.trackAddCompetitorManually(newCompetitor.name, competitorsList.size);
        }

        const defaultCompetitor = {
            ...newCompetitor,
            isAiGenerated: false,
            reason: translate(competitorsStepTexts.defaultReason),
        };

        setCompetitorsList(getUpdatedMap(defaultCompetitor, competitorsList));
        onCompetitorSelect(defaultCompetitor);

        eventForScroll.current = false;
        autocompleteRef.current.closePopup();
    };

    const excluded = [...getSelectedCompetitorsNamesList(selectedCompetitors), selectedPrimaryEntity?.name];

    const onClick = (selected: ISite) => {
        resetPrompt();
        setPrimary({ image: selected.image, name: selected.name, icon: "" });
    };

    const onPaste = async (e: ClipboardEvent) => {
        const { updatedSelectedCompetitorsList, updatedCompetitorsList } = await onPasteDataUpdater(e);

        setSelectedCompetitors(updatedSelectedCompetitorsList);
        setCompetitorsList(updatedCompetitorsList);

        autocompleteRef.current.closePopup();

        eventForScroll.current = false;
    };

    const isDisabledCountryFilter = isAISuggestionLoading || isLimitReached || !selectedPrimaryEntity;
    const isDisabled = isDisabledCountryFilter || !selectedCountry;

    const isBigList = !showWebsiteSelect && !isMultiCountry;

    const shouldScroll = !isLoadingPaste && !isAISuggestionLoading && eventForScroll.current;

    useEffect(
        () => () => {
            if (!isOpen) {
                resetPrompt();
                setCompetitorsList(new Map());
                onCountrySelect(undefined);
            }
        },
        [isOpen],
    );

    return (
        <Wrapper
            data-automation={CompetitorsStepKeys.mainWrapper}
            id={COMPETITORS_WRAPPER_ID}
        >
            <SelectedDomainTitle data-automation={CompetitorsStepKeys.title}>
                {translate(TITLE_TEXT)}
            </SelectedDomainTitle>

            <WrappedSelectGroup>
                {showWebsiteSelect ? (
                    <WrappedSelect data-automation={CompetitorsStepKeys.selectorsWrapper}>
                        <SelectCountryTitle data-automation={CompetitorsStepKeys.selectorsTitle}>
                            {translate(competitorsStepTexts.selectPrimary)}
                        </SelectCountryTitle>

                        <div data-automation={CompetitorsStepKeys.websiteInputWrapper}>
                            <SC.StyledWebsiteInput
                                isFocused
                                excludeList={excluded}
                                isDisabled={isAISuggestionLoading || isLoadingPaste}
                                placeholder={competitorsStepTexts.primaryPlaceholder}
                                onClick={onClick}
                                selectedWebsite={selectedPrimaryEntity}
                            />
                        </div>
                    </WrappedSelect>
                ) : null}

                {isMultiCountry ? (
                    <WrappedSelect>
                        <SelectCountryTitle data-automation={CompetitorsStepKeys.countrySelectTitle}>
                            {translate(competitorsStepTexts.selectCountry)}
                        </SelectCountryTitle>

                        <div data-automation={CompetitorsStepKeys.countrySelectDropdown}>
                            <CountryDropdown
                                onCountrySelect={onCountrySelect}
                                isDisabled={isDisabledCountryFilter}
                                initialSelectedCountry={selectedCountry}
                            />
                        </div>
                    </WrappedSelect>
                ) : null}
            </WrappedSelectGroup>

            <SC.CompetitorsDropdownWrapper>
                <CompetitorsLimitText data-automation={CompetitorsStepKeys.selectCompetitorsLimit}>
                    {capitalize(translate(competitorsStepTexts.selectCompetitors))}

                    <SC.CompetitorsLimit>
                        <b>{selectedCompetitors.length}</b>/{maxCompetitors}
                    </SC.CompetitorsLimit>
                </CompetitorsLimitText>

                <CompetitorsDropdown
                    excludedCompetitors={excluded}
                    autocompleteRef={autocompleteRef}
                    onCompetitorAdd={onCompetitorAdd}
                    onPaste={onPaste}
                    isDisabled={isDisabled}
                />
            </SC.CompetitorsDropdownWrapper>

            <SuggestedItemsListWrapper>
                {isAISuggestionLoading || isLoadingPaste ? (
                    <LoaderWrapper data-automation={CompetitorsStepKeys.loaderWrapper}>
                        <AILoader
                            title={translate(competitorsStepTexts.loaderTitle)}
                            subTitle={translate(competitorsStepTexts.loaderSubTitle)}
                        />
                    </LoaderWrapper>
                ) : (
                    <CompetitorsList
                        showWebsiteSelect={showWebsiteSelect}
                        selectedCompetitors={selectedCompetitors}
                        competitorsList={competitorsList}
                        onSelectCompetitor={onCompetitorSelect}
                        isLimitReached={isLimitReached}
                        isBigList={isBigList}
                        shouldScroll={shouldScroll}
                    />
                )}
            </SuggestedItemsListWrapper>

            <SC.AIInputWrapper>
                <AIChips
                    onClick={onClickChip}
                    isDisabled={isDisabled}
                    isAISuggestionLoading={isAISuggestionLoading}
                    suggestedPrompts={suggestedPrompts}
                />

                <AIInput
                    isLoading={isAISuggestionLoading}
                    onChange={onChange}
                    handleSetPrompt={handleSetPrompt}
                    promptText={promptText}
                    isDisabled={isDisabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            </SC.AIInputWrapper>
        </Wrapper>
    );
};

export default CompetitorsStep;
